import { Box, Paper, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { AudioFileCategory, AudioFileItem } from '@aviation/cabinapp-cabincast-sdk';
import { AUDIO_FILE_CATEGORIES } from './AudioFile';

export interface IAudioFileCategoryChartProps {
    data?: AudioFileItem[];
    selected?: AudioFileCategory;
    onSelect?: (category: AudioFileCategory) => void;
}

// Brand-aligned palette (same shades as the UserFeedback charts).
const CATEGORY_COLORS: { [key in AudioFileCategory]: string } = {
    music: '#70CBF4',
    safety: '#092A5E',
    service: '#1890FF',
    emergency: '#637381',
};

interface ChartDatum {
    category: AudioFileCategory;
    name: string;
    value: number;
}

function AudioFileCategoryChart({ data = [], selected, onSelect }: IAudioFileCategoryChartProps) {
    const chartData = useMemo<ChartDatum[]>(() => {
        return AUDIO_FILE_CATEGORIES
            .map(c => ({ category: c.value, name: c.label, value: data.filter(o => o.Category === c.value).length }))
            .filter(d => d.value > 0);
    }, [data]);

    return (
        <Paper sx={{ p: 2 }} elevation={3}>
            <Typography variant='subtitle1' component='h2' sx={{ textTransform: 'uppercase', mb: 1 }}>
                Categories
            </Typography>
            {chartData.length === 0 ? (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>No data</Typography>
                </Box>
            ) : (
                <ResponsiveContainer width='100%' height={240}>
                    <PieChart>
                        <Pie
                            data={chartData} dataKey='value' nameKey='name' cx='50%' cy='50%' outerRadius={80}
                            style={{ cursor: 'pointer' }}
                            onClick={(entry: ChartDatum) => onSelect?.(entry.category)}
                        >
                            {chartData.map(entry => (
                                <Cell
                                    key={entry.category}
                                    fill={CATEGORY_COLORS[entry.category]}
                                    fillOpacity={selected === undefined || selected === entry.category ? 1 : 0.4}
                                />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </Paper>
    );
}

export default AudioFileCategoryChart;
